import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Paper, 
  Box,
  Fade,
  Grow
} from "@material-ui/core";
import { Link } from "react-router-dom";
import DashboardMui from "../../dashboard/DashboardMui";
import AssessmentIcon from "@material-ui/icons/Assessment";
import BarChartIcon from "@material-ui/icons/BarChart";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import ReceiptIcon from "@material-ui/icons/Receipt";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import CharSquare from "../../../images/chart-square.svg";
import useBDocTranslator from "../../hooks/useBDocTranslator";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    borderRadius: 20,
    border: "none",
    boxShadow: "none",
  },
  gridContainer: {
    marginTop: theme.spacing(2),
  },
  cardWrapper: {
    textDecoration: "none",
    color: "inherit",
    display: "block",
    height: "100%",
  },
  card: {
    height: 240,
    borderRadius: 20,
    background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid rgba(7, 108, 216, 0.1)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
    transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: "linear-gradient(90deg, #076cd8, #4a90e2)",
      transform: "scaleX(0)",
      transformOrigin: "left",
      transition: "transform 0.3s ease",
    },
    "&:hover": {
      transform: "translateY(-12px) scale(1.02)",
      boxShadow: "0 20px 60px rgba(7, 108, 216, 0.25)",
      "&::before": {
        transform: "scaleX(1)",
      },
      "& $iconContainer": {
        transform: "scale(1.1) rotate(5deg)",
        background: "linear-gradient(135deg, #076cd8, #4a90e2)",
        "& $icon": {
          color: "white",
        },
      },
      "& $title": {
        color: "#076cd8",
      },
    },
  },
  cardContent: {
    padding: theme.spacing(3),
    textAlign: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(7, 108, 216, 0.1), rgba(74, 144, 226, 0.1))",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2),
    transition: "all 0.4s ease",
    border: "2px solid rgba(7, 108, 216, 0.2)",
  },
  icon: {
    fontSize: "2.5rem",
    color: "#076cd8",
    transition: "all 0.3s ease",
  },
  title: {
    fontSize: "1.25rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#333",
    transition: "color 0.3s ease",
    lineHeight: 1.3,
  },
  description: {
    fontSize: "0.9rem",
    color: "#666",
    fontWeight: 400,
    opacity: 0.8,
    lineHeight: 1.4,
  },
  decorativeElement: {
    position: "absolute",
    bottom: -20,
    right: -20,
    width: 60,
    height: 60,
    borderRadius: "50%",
    background: "linear-gradient(135deg, rgba(7, 108, 216, 0.05), rgba(74, 144, 226, 0.05))",
    transition: "all 0.3s ease",
  },
}));

const ReportsSelection = () => {
  const classes = useStyles();
  const { t, changeDirectLanguage } = useBDocTranslator();
  
  const reports = [
    {
      title: t("Appointment Income"),
      description: t("Track revenue and financial metrics from appointments"),
      path: "/admin/report/appointments/income",
      icon: MonetizationOnIcon,
      color: "#4CAF50",
    },
    {
      title: t("Patient Reports"),
      description: t("Comprehensive patient data and analytics"),
      path: "/admin/report/patients",
      icon: ReceiptIcon,
      color: "#2196F3",
    },
    {
      title: t("Appointment Reports"),
      description: t("Detailed appointment statistics and insights"),
      path: "/admin/report/appointment",
      icon: AssessmentIcon,
      color: "#FF9800",
    },
  ];

  useEffect(() => {
    changeDirectLanguage("es");
  }, [changeDirectLanguage]);

  return (
    <DashboardMui
      sectionTitle={t("Reports Selection")}
      icon={CharSquare}
      description={t(
        "View and manage various reports related to appointments, patients, and more."
      )}
    >
      <Paper elevation={0} className={classes.root}>
        <Grid container spacing={4} className={classes.gridContainer}>
          {reports.map((report, index) => {
            const IconComponent = report.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={report.title}>
                <Grow
                  in={true}
                  timeout={600 + index * 200}
                  style={{ transformOrigin: "center bottom" }}
                >
                  <Box>
                    <Link to={report.path} className={classes.cardWrapper}>
                      <Card className={classes.card} elevation={0}>
                        <CardContent className={classes.cardContent}>
                          <Box>
                            <Box className={classes.iconContainer}>
                              <IconComponent className={classes.icon} />
                            </Box>
                            <Typography variant="h6" className={classes.title}>
                              {report.title}
                            </Typography>
                            <Typography className={classes.description}>
                              {report.description}
                            </Typography>
                          </Box>
                          <Box className={classes.decorativeElement} />
                        </CardContent>
                      </Card>
                    </Link>
                  </Box>
                </Grow>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    </DashboardMui>
  );
};

export default ReportsSelection;
